import type { DeliverableCheck } from '@/types/portal';

/** Buckets every deliverable status into the review states the tab cares about. */
export type DeliverableGroup =
  | 'awaiting'
  | 'changes'
  | 'approved'
  | 'draft'
  | 'exception';

const GROUP_BY_STATUS: Record<string, DeliverableGroup> = {
  'Pending review': 'awaiting',
  Viewed: 'awaiting',
  'Deliverable Submitted': 'awaiting',
  'Awaiting approval': 'awaiting',
  Review: 'awaiting',
  'Changes requested': 'changes',
  Approved: 'approved',
  Paid: 'approved',
  Draft: 'draft',
  'Deadline at risk': 'exception',
  'Deadline missed': 'exception',
  'Deliverable missing': 'exception',
  Rejected: 'exception',
};

export function groupOf(status: string): DeliverableGroup {
  return GROUP_BY_STATUS[status] ?? 'awaiting';
}

export interface CheckSummary {
  passes: number;
  warns: number;
  fails: number;
}

export function summarizeChecks(checks: DeliverableCheck[]): CheckSummary {
  return checks.reduce<CheckSummary>(
    (acc, check) => {
      if (check.status === 'fail') acc.fails += 1;
      else if (check.status === 'warn') acc.warns += 1;
      else acc.passes += 1;
      return acc;
    },
    { passes: 0, warns: 0, fails: 0 },
  );
}

/**
 * A piece is "clean" only when every single brief check passes — no fails and
 * no warnings. This is deliberately strict so bulk approval never waves through
 * a piece that still carries an open concern.
 */
export function allChecksPass(checks: DeliverableCheck[]): boolean {
  return checks.length > 0 && checks.every((check) => check.status === 'pass');
}
